import { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import {
	invoiceEditApplyTemplateModalInvoiceTemplateFragment,
	InvoiceTemplate,
} from './types';
import { useBlockingRouter } from '../../hooks/useBlockingRouter';
import { useInvoiceTemplateFromUrlParamIfProvided_GetInvoiceTemplateWhereLazyQuery } from '#types';
import { useToast } from '../../hooks/useToast';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceTemplateFromUrlParamIfProvided_getInvoiceTemplateWhere = gql`
	query InvoiceTemplateFromUrlParamIfProvided_getInvoiceTemplateWhere(
		$where: InvoiceTemplateWhereUniqueInput!
	) {
		getInvoiceTemplateWhere(where: $where) {
			...invoiceEditApplyTemplateModalInvoiceTemplate
		}
	}
	${ invoiceEditApplyTemplateModalInvoiceTemplateFragment }
`;

/**
 * A hook to help manage URL params with respect to invoice templates, and
 * load the template if necessary.
 *
 * If the url param exists
 *  - it's removed
 *  - it's used to load the template data
 *  - if the template data is loaded, a callback is executed with the template
 *    data as an argument
 */
export const useInvoiceTemplateFromUrlParamIfProvided = (
	callback: ( invoiceTemplate: InvoiceTemplate ) => void
) => {
	const router = useBlockingRouter();
	const { showError, showSuccess } = useToast();
	const [ invoiceTemplateId, setInvoiceTemplateId ] = useState<
		string | undefined
	>();
	const [ getInvoiceTemplateWhere, { loading } ] =
		useInvoiceTemplateFromUrlParamIfProvided_GetInvoiceTemplateWhereLazyQuery();

	useEffect( () => {
		// clean up the URL, but only trigger a re-render once
		const invoiceTemplateId =
			typeof router.query.invoiceTemplateId === 'string'
				? router.query.invoiceTemplateId
				: undefined;
		const asPath = router.asPath;
		const cleanPath = asPath
			.replace( /&?invoiceTemplateId=[^&]+/, '' )
			.replace( /\?&/, '?' )
			.replace( /\?$/, '' );
		if ( cleanPath !== asPath ) {
			router.replace( cleanPath );
		}
		setInvoiceTemplateId( ( prevState ) =>
			prevState ? prevState : invoiceTemplateId
		);
	}, [ router ] );

	useEffect( () => {
		// if there's an invoiceTemplateId, use it and clear it to avoid a render loop
		const checkUrlAndActAccordingly = async () => {
			if ( invoiceTemplateId ) {
				const id = invoiceTemplateId;
				setInvoiceTemplateId( undefined );
				const response = await getInvoiceTemplateWhere( {
					variables: { where: { id } },
				} );
				if ( response.error ) {
					showError( response.error );
				} else if ( response.data?.getInvoiceTemplateWhere ) {
					showSuccess( 'Template successfully applied' );
					callback( response.data?.getInvoiceTemplateWhere );
				}
			}
		};
		checkUrlAndActAccordingly();
	}, [
		invoiceTemplateId,
		callback,
		router,
		getInvoiceTemplateWhere,
		showError,
		showSuccess,
	] );

	return { loading };
};
